/**
 * The hero vehicle image for the move-details info section.
 *
 * Mirrors the scenario dashboard's leg-photo rule for a single move: within a phase
 * the hero is the photo the phase's workflow marks `is_hero`, then an **exact**
 * `driver-front` (never a `driver-front-wheel` standing in for it). The delivery
 * hero wins as soon as it exists; otherwise the pickup hero. With neither, a short
 * list of standard exterior shots stands in (delivery preferred per step), and with
 * nothing at all the caller shows its placeholder.
 *
 * Photos arrive already split by phase (`pickup_photos` / `delivery_photos`), so
 * there is no grouping to do here — unlike the scenario version.
 */

/** A vehicle photo, narrowed to what hero selection reads. */
export interface MoveHeroPhoto {
  step_id?: string | null;
  url?: string | null;
}

/** A node in a workflow's `steps` jsonb tree; only the read fields are typed. */
interface MoveHeroWorkflowStep {
  id?: string | null;
  type?: string | null;
  config?: { is_hero?: boolean | null } | null;
  steps?: unknown;
}

/** Exterior shots that stand in for the hero, after is_hero and driver-front. */
const FALLBACK_STEP_IDS = ['passenger-front', 'driver-side-full-view', 'passenger-side-full-view'];

/**
 * The step id a workflow designates as its hero: the first photo step, depth-first,
 * whose `config.is_hero` is true. Group nodes are descended into. Accepts the raw
 * `steps` jsonb (unknown) and returns null when it names no hero.
 */
export const findHeroStepId = (steps: unknown): string | null => {
  if (!Array.isArray(steps)) return null;
  for (const node of steps) {
    if (!node || typeof node !== 'object') continue;
    const step = node as MoveHeroWorkflowStep;
    if (step.type === 'photo' && step.config?.is_hero === true && step.id) return step.id;
    const nested = findHeroStepId(step.steps);
    if (nested) return nested;
  }
  return null;
};

/** The url of the first photo with the given step id, or null. */
const urlForStep = (photos: (MoveHeroPhoto | null)[] | null | undefined, stepId: string | null): string | null =>
  stepId ? photos?.find(photo => photo?.step_id === stepId)?.url ?? null : null;

/** A phase's hero: its workflow's is_hero capture, then an exact driver-front. */
const phaseHeroUrl = (
  photos: (MoveHeroPhoto | null)[] | null | undefined,
  steps: unknown
): string | null => urlForStep(photos, findHeroStepId(steps)) ?? urlForStep(photos, 'driver-front');

export const resolveMoveHeroImageUrl = ({
  deliveryPhotos,
  pickupPhotos,
  deliverySteps,
  pickupSteps,
}: {
  deliveryPhotos?: (MoveHeroPhoto | null)[] | null;
  pickupPhotos?: (MoveHeroPhoto | null)[] | null;
  deliverySteps?: unknown;
  pickupSteps?: unknown;
}): string => {
  // The delivery hero wins once it exists, then the pickup hero.
  const hero = phaseHeroUrl(deliveryPhotos, deliverySteps) ?? phaseHeroUrl(pickupPhotos, pickupSteps);
  if (hero) return hero;

  // The remaining standard exterior shots, delivery preferred per step.
  for (const stepId of FALLBACK_STEP_IDS) {
    const url = urlForStep(deliveryPhotos, stepId) ?? urlForStep(pickupPhotos, stepId);
    if (url) return url;
  }
  return '';
};
